<template>
  <EditorPanel :loading-save="loadingSave" :disabled="!isDescriptionValid" @confirm="saveSettings">
    <EditorSectionLabel :text="$t('harmony.add-colorDescription')" class="ma-0 mb-1" />
    <v-color-picker
      v-model="localDescColor"
      dot-size="25"
      hide-inputs
      hide-mode-switch
      swatches-max-height="200"
      canvas-height="100"
      class="remove-preview mx-auto"
    />

    <div class="d-flex justify-space-between align-center mt-3 mb-1">
      <EditorSectionLabel :text="$t('harmony.add-description')" class="ma-0" />
      <v-menu
        v-model="showEmojiMenu"
        :close-on-content-click="false"
        offset-y
        top
        left
        nudge-bottom="6"
        transition="scale-transition"
        max-width="260"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            small
            color="#9BA9BD"
            v-bind="attrs"
            v-on="on"
            :aria-label="$t('harmony.toggle-emoji-picker')"
            class="emoji-toggle-btn"
          >
            <v-icon small>mdi-emoticon-happy-outline</v-icon>
          </v-btn>
        </template>

        <v-card class="emoji-picker-card rounded-lg pa-2" elevation="6">
          <div class="d-flex justify-space-around pb-1 emoji-cat-bar">
            <v-btn
              v-for="(cat, idx) in emojiCategories"
              :key="idx"
              icon
              x-small
              :class="{ 'emoji-cat-btn--active': activeCategoryIndex === idx }"
              @click="activeCategoryIndex = idx"
            >
              <span class="emoji-cat-icon">{{ cat.icon }}</span>
            </v-btn>
          </div>

          <div class="emoji-grid mt-2">
            <span
              v-for="(emoji, eIdx) in emojiCategories[activeCategoryIndex].emojis"
              :key="eIdx"
              class="emoji-item"
              @click="appendEmoji(emoji)"
            >
              {{ emoji }}
            </span>
          </div>
        </v-card>
      </v-menu>
    </div>

    <v-textarea
      v-model="localDescription"
      color="#9BA9BD"
      :placeholder="$t('harmony.enter')"
      outlined
      dense
      counter
      maxlength="200"
      :rules="[rules.containUrl]"
      rows="3"
      @input="handleDescriptionInput"
      class="mt-1"
    />

    <EditorSectionLabel text="Formato" class="mt-1 mb-1" />
    <EditorButtonGroup
      class="mb-2"
      :options="[
        { label: 'B', value: 'bold', ariaLabel: $t('harmony.format-bold') },
        { label: 'I', value: 'italic', ariaLabel: $t('harmony.format-italic') },
        { label: 'U', value: 'underline', ariaLabel: $t('harmony.format-underline') }
      ]"
      :active="activeFormat"
      :color="'#9BA9BD'"
      @select="toggleFormat"
    >
      <template v-slot:option="{ option, isActive }">
        <v-icon
          v-if="option.value === 'bold'"
          small
          aria-hidden="true"
          :color="isActive ? 'white' : '#9BA9BD'"
        >mdi-format-bold</v-icon>
        <v-icon
          v-else-if="option.value === 'italic'"
          small
          aria-hidden="true"
          :color="isActive ? 'white' : '#9BA9BD'"
        >mdi-format-italic</v-icon>
        <v-icon
          v-else-if="option.value === 'underline'"
          small
          aria-hidden="true"
          :color="isActive ? 'white' : '#9BA9BD'"
        >mdi-format-underline</v-icon>
      </template>
    </EditorButtonGroup>

    <EditorSectionLabel text="Posición" class="mt-1 mb-1" />
    <EditorButtonGroup
      class="mb-1"
      :options="[
        { label: 'L', value: 'left', ariaLabel: $t('harmony.align-left') },
        { label: 'C', value: 'center', ariaLabel: $t('harmony.align-center') },
        { label: 'R', value: 'right', ariaLabel: $t('harmony.align-right') }
      ]"
      :active="textAlignment"
      :color="'#9BA9BD'"
      @select="setAlignment"
    >
      <template v-slot:option="{ option, isActive }">
        <v-icon
          v-if="option.value === 'left'"
          small
          aria-hidden="true"
          :color="isActive ? 'white' : '#9BA9BD'"
        >mdi-format-align-left</v-icon>
        <v-icon
          v-else-if="option.value === 'center'"
          small
          aria-hidden="true"
          :color="isActive ? 'white' : '#9BA9BD'"
        >mdi-format-align-center</v-icon>
        <v-icon
          v-else-if="option.value === 'right'"
          small
          aria-hidden="true"
          :color="isActive ? 'white' : '#9BA9BD'"
        >mdi-format-align-right</v-icon>
      </template>
    </EditorButtonGroup>
  </EditorPanel>
</template>

<script>
import EditorPanel from './EditorPanel.vue'
import EditorButtonGroup from './shared/EditorButtonGroup.vue'
import EditorSectionLabel from './shared/EditorSectionLabel.vue'

export default {
  name: 'EditorDescriptionPanel',
  components: { EditorPanel, EditorButtonGroup, EditorSectionLabel },
  props: {
    siteSetting: {
      type: Object,
      required: true
    },
    loadingSave: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showEmojiMenu: false,
      activeCategoryIndex: 0,
      emojiCategories: [
        {
          name: 'Smileys',
          icon: '😊',
          emojis: [
            '😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '🥹', '☺️',
            '😊', '😇', '🙂', '😉', '😌', '😍', '🥰', '😘', '😋', '😛',
            '😜', '🤪', '😎', '🤩', '🥳', '😏', '🤔', '🤫', '🤭', '🤗',
            '🤠', '😈', '🤖', '👻', '💀', '🤡', '💩', '👀', '✨', '🔥'
          ]
        },
        {
          name: 'Hands',
          icon: '👍',
          emojis: [
            '👍', '👎', '👏', '🙌', '👐', '🤝', '👊', '✌️', '🤞', '🤟',
            '🤘', '👌', '🤌', '🤏', '👈', '👉', '👆', '👇', '☝️', '✋',
            '🤚', '🖐️', '🖖', '👋', '🤙', '💪', '✍️', '🙏'
          ]
        },
        {
          name: 'Hearts',
          icon: '❤️',
          emojis: [
            '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔',
            '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '🌟',
            '💫', '💥', '💯', '👑', '💎'
          ]
        },
        {
          name: 'Fun',
          icon: '🎉',
          emojis: [
            '🎉', '🎊', '🎈', '🎁', '🏆', '🥇', '🥈', '🥉', '⚽', '🏀',
            '🏈', '🎾', '🎮', '🎯', '🎲', '🎨', '🎬', '🎤', '🎧', '🎼',
            '🎹', '🥁', '🎷', '🎸', '🎺', '🌴', '🌸', '🌺', '🍀', '☀️',
            '🌙', '⭐', '🌈'
          ]
        },
        {
          name: 'Objects',
          icon: '🚀',
          emojis: [
            '🚀', '✈️', '🚗', '📱', '💻', '📷', '📹', '💡', '💰', '💵',
            '🛍️', '✉️', '📌', '📍', '🔒', '🔑', '☕', '🍺', '🥂', '🍷',
            '🍕', '🍔', '🍟', '🌮', '🍣', '🍰', '🍦', '🍩', '🍫'
          ]
        }
      ],
      localDescription: this.siteSetting.profile.description || '',
      localDescColor: this.siteSetting.profile.desc_color || '#373535',
      textFormats: this.siteSetting.profile.description_style?.formats || [],
      textAlignment: this.siteSetting.profile.description_style?.alignment || 'center',
      rules: {
        containUrl: value => {
          if (!value) return true;
          const urlRegex = RegExp(/(https?:\/\/[^\s]+|www\.[^\s]+|\b(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(?:\/[^\s]*)?\b|(?<![a-zA-Z])(?:\+?\d{1,3})?[-.\s]?\(?\d{1,4}\)?[-.\s]?\d{1,4}[-.\s]?\d{1,9}\b(?!\d)(?=\s|$)|(?<!\d)(?=.*[a-zA-Z])\S*\d{5,}\S*)/g);
          return !urlRegex.test(value) || this.$t('harmony.contains-url');
        }
      }
    }
  },
  computed: {
    isDescriptionValid() {
      return this.rules.containUrl(this.localDescription) === true
    },
    activeFormat() {
      if (this.textFormats.length === 0) return null
      return this.textFormats[this.textFormats.length - 1]
    }
  },
  watch: {
    'siteSetting.profile.description'(newVal) {
      this.localDescription = newVal || ''
    },
    'siteSetting.profile.desc_color'(newVal) {
      this.localDescColor = newVal || '#373535'
    },
    'siteSetting.profile.description_style': {
      handler(newVal) {
        this.textFormats = newVal?.formats || []
        this.textAlignment = newVal?.alignment || 'center'
      },
      deep: true
    }
  },
  methods: {
    saveSettings() {
      this.$emit('update:description', this.localDescription)
      this.$emit('update:desc-color', this.localDescColor)
      this.$emit('update:description-style', {
        formats: [...this.textFormats],
        alignment: this.textAlignment
      })
      this.$emit('save-settings')
    },
    toggleFormat(format) {
      const index = this.textFormats.indexOf(format)
      if (index > -1) {
        this.textFormats.splice(index, 1)
      } else {
        this.textFormats.push(format)
      }
    },
    setAlignment(alignment) {
      this.textAlignment = alignment
    },
    appendEmoji(emojiChar) {
      if ((this.localDescription || '').length + emojiChar.length <= 200) {
        this.localDescription = (this.localDescription || '') + emojiChar
        this.handleDescriptionInput()
      }
    },
    handleDescriptionInput() {
      this.$emit('description-input', this.localDescription)
    }
  }
}
</script>

<style scoped>
@import './editor-panel.css';

.emoji-toggle-btn {
  transition: transform 0.15s ease;
}

.emoji-toggle-btn:hover {
  transform: scale(1.15);
  color: var(--editor-color-primary, #8e84c0) !important;
}

.emoji-picker-card {
  max-width: 250px;
  background-color: #ffffff;
}

.emoji-cat-bar {
  border-bottom: 1px solid #eee;
}

.emoji-cat-btn--active {
  background-color: #f0edf9 !important;
  border-radius: 4px;
}

.emoji-cat-icon {
  font-size: 14px;
  line-height: 1;
}

.emoji-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 2px;
  max-height: 150px;
  overflow-y: auto;
}

.emoji-item {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  cursor: pointer;
  border-radius: 4px;
  padding: 3px;
  user-select: none;
  transition: background-color 0.15s, transform 0.15s;
}

.emoji-item:hover {
  background-color: #f0edf9;
  transform: scale(1.2);
}
</style>
